import { useState, useMemo, useCallback, useEffect } from "react";
import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import { Search } from "@/components/shared/inputs/search";
import { PlusIcon } from "@radix-ui/react-icons";
import { Loader2Icon } from "lucide-react";
import { useBrandApi } from "@/v2/entities/brand/Brand.api";
import {
  TenantBrand,
  GetBrandsResponse,
} from "@/v2/entities/brand/Brand.model";
import { BrandItem } from "./BrandItem";
import { CreateBrandDialog } from "./CreateBrandDialog";

export function Brands() {
  const brandApi = useBrandApi();
  const [brands, setBrands] = useState<TenantBrand[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchBrands = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await brandApi.getBrands();
      setBrands(response || []);
    } catch (error) {
      // log error
      // eslint-disable-next-line no-console
      console.error("failed to fetch brands:", error);
      setBrands([]);
    } finally {
      setIsLoading(false);
    }
  }, [brandApi]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const refetch = useCallback(() => {
    fetchBrands();
  }, [fetchBrands]);

  const filteredBrands = useMemo(() => {
    if (!brands || !searchPhrase) return brands || [];
    return brands.filter(
      (brand: TenantBrand) =>
        brand.name.toLowerCase().includes(searchPhrase.toLowerCase()) ||
        brand.subdomain.toLowerCase().includes(searchPhrase.toLowerCase())
    );
  }, [brands, searchPhrase]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" className="h-full">
        <Loader2Icon className="animate-spin" />
      </Flex>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center p-4 border-b">
        <Text as="div" size="4" weight="medium">
          Brands
        </Text>
      </div>

      <div className="flex-1 px-8 py-8">
        <Flex direction="column" gap="8">
          <Flex direction="row" gap="2" justify="between" align="center">
            <Flex direction="row" gap="2" align="center">
              <Flex gap="2" align="center">
                <Text size="3" weight="medium">
                  Brands
                </Text>
                <Badge color="gray">{filteredBrands.length}</Badge>
              </Flex>
              <Search
                initialValue={searchPhrase}
                placeholder="Search brands"
                setSearchPhrase={setSearchPhrase}
                debounce={50}
              />
            </Flex>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={() => setCreateDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <PlusIcon />
              <Text as="span" size="2" weight="medium">
                Create Brand
              </Text>
            </Button>
          </Flex>

          <Table.Root>
            <Table.Header>
              <Table.Row className="text-gray-500 font-normal">
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  <Text as="span" size="2" color="gray">
                    Brand Name
                  </Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  <Text as="span" size="2" color="gray">
                    Subdomain
                  </Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  <Text as="span" size="2" color="gray">
                    External ID
                  </Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-1/6">
                  <Text as="span" size="2" color="gray">
                    Created
                  </Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-[80px] text-right">
                  <Text as="span" size="2" color="gray">
                    Actions
                  </Text>
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredBrands.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Flex
                      align="center"
                      justify="center"
                      py="6"
                      direction="column"
                      gap="2"
                    >
                      <Text size="2" color="gray">
                        {brands?.length === 0
                          ? "No brands found"
                          : "No brands match your search"}
                      </Text>
                      <Text size="2" color="gray">
                        {brands?.length === 0 &&
                          "Create a new brand to get started"}
                      </Text>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )}
              {filteredBrands
                .slice()
                .sort((a: TenantBrand, b: TenantBrand) => {
                  return a.name
                    .toLowerCase()
                    .localeCompare(b.name.toLowerCase());
                })
                .map((brand: TenantBrand) => (
                  <BrandItem key={brand.id} brand={brand} refetch={refetch} />
                ))}
            </Table.Body>
          </Table.Root>
        </Flex>
      </div>

      <CreateBrandDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        refetch={refetch}
      />
    </div>
  );
}
